// M21b "Ideas" templates 13–26 (owner brief 2026-10-12): more fast-food ad devices in the restaurant's own
// brand — the sliced dish, the exploded layers, the drip, the countdown number, the price as hero, the mirror
// 2x1, the question and answer, the speech bubble, the till receipt, the map pin, the stack of prints, the
// clock, the tiny dish, the day of the week. Same rules as ideas.ts. Nothing restaurant-specific is baked in:
// the restaurant name, location line, colours and logo come from My brand; every text is an editable field.
import type { Builder, TemplateDef } from './dsl';
import { esOnly } from '../ad/fields';
import { DEEP, SOFT, tx } from './ideas';

const bi = (es: string, en: string) => ({ es, en });
const INK = '#1a1a1a';
const MUTED = '#6b6b6b';
const WHITE = '#ffffff';

/** A tilted print: a white card with the photo inside, both turned around the card's centre. */
function print(
  b: Builder,
  cx: number,
  cy: number,
  deg: number,
  crop: { zoom: number; x: number; y: number },
) {
  b.rotate(b.rect(cx - 280, cy - 320, 560, 640, { fill: WHITE }), deg, cx, cy);
  b.rotate(
    b.photo(cx - 250, cy - 290, 500, 500, { zoom: crop.zoom, offsetX: crop.x, offsetY: crop.y }),
    deg,
    cx,
    cy,
  );
}

export const IDEAS_TEMPLATES_2: TemplateDef[] = [
  // 13. Split in half: the dish sliced down the middle, the halves pulled apart.
  {
    id: 'ideas-split',
    family: 'ideas',
    slot: 'cutout',
    category: 'post',
    format: 'ig-square',
    name: bi('Ideas — partido a la mitad', 'Ideas — split in half'),
    ad: { headline: esOnly('Por dentro.') },
    adAny: { headline: esOnly('Hecho al momento, como debe ser.') },
    build: (b) => {
      b.bg('primary');
      tx(b, 'headline', 70, 50, 800, 140, { family: 'Anton', size: 120, color: 'onPrimary' });
      const half = { y0: 0, y1: 1 };
      // M21b review: a clear gap of 15% of the width (162 px) between the cut faces.
      b.rotate(
        b.photo(30, 230, 430, 780, { fit: 'contain', slice: { x0: 0, x1: 0.5, ...half }, shadow: DEEP }),
        -4,
        245,
        620,
      );
      b.rotate(
        b.photo(622, 230, 430, 780, { fit: 'contain', slice: { x0: 0.5, x1: 1, ...half }, shadow: DEEP }),
        4,
        837,
        620,
      );
      b.logo(920, 50, 110, 110);
    },
  },
  // 14. Floating layers: the dish in horizontal layers, spaced apart; the price below.
  {
    id: 'ideas-layers',
    family: 'ideas',
    slot: 'cutout',
    category: 'post',
    format: 'ig-portrait',
    name: bi('Ideas — capas flotantes', 'Ideas — floating layers'),
    build: (b) => {
      b.bg('white');
      const cuts = [0, 0.32, 0.52, 0.74, 1];
      const total = 860,
        gap = 40;
      let y = 60;
      for (let i = 0; i < 4; i++) {
        const h = Math.round(total * (cuts[i + 1]! - cuts[i]!));
        b.photo(90, y, 900, h, {
          fit: 'contain',
          slice: { x0: 0, x1: 1, y0: cuts[i]!, y1: cuts[i + 1]! },
          shadow: SOFT,
        });
        y += h + gap;
      }
      tx(b, 'price', 90, 1120, 640, 170, { family: 'Anton', size: 150, color: 'accentText' });
      b.logo(900, 1180, 130, 130, { onLight: true });
    },
  },
  // 15. Drip close-up: a tight crop of the melting part; the price below the drips.
  {
    id: 'ideas-drip',
    family: 'ideas',
    slot: 'full',
    category: 'post',
    format: 'ig-square',
    name: bi('Ideas — goteo', 'Ideas — drip close-up'),
    build: (b) => {
      b.bg('white');
      b.photo(0, 0, 1080, 700, { zoom: 2.4, offsetY: 0.1 });
      b.rect(0, 680, 1080, 40, { fill: 'primary' });
      for (const [x, w, h] of [
        [90, 60, 150],
        [240, 70, 230],
        [420, 56, 110],
        [600, 80, 260],
        [800, 60, 170],
        [950, 70, 120],
      ] as const)
        b.rect(x, 690, w, h, { fill: 'primary', r: w / 2 });
      tx(b, 'price', 60, 960, 640, 100, { family: 'Anton', size: 96, color: 'accentText' });
      b.logo(930, 940, 120, 120, { onLight: true });
    },
  },
  // 16. Countdown number: a giant number behind the dish.
  {
    id: 'ideas-count',
    family: 'ideas',
    slot: 'cutout',
    category: 'story',
    format: 'story',
    name: bi('Ideas — cuenta regresiva', 'Ideas — countdown number'),
    ad: { headline: esOnly('Últimas horas.'), tagline: esOnly('3') },
    build: (b) => {
      b.bg('secondary');
      tx(b, 'tagline', 60, 330, 960, 1180, {
        family: 'Anton',
        size: 900,
        color: 'accentOnSecondary',
        align: 'center',
        valign: 'top',
        leading: 0.9,
      });
      b.photo(60, 1040, 960, 800, { fit: 'contain', zoom: 0.95, shadow: DEEP });
      tx(b, 'headline', 60, 290, 800, 100, {
        family: 'Oswald',
        weight: 700,
        size: 90,
        color: 'onSecondary',
      });
      b.logo(890, 290, 130, 130);
    },
  },
  // 17. Price as hero: the price fills the frame; the dish overlaps the numbers.
  {
    id: 'ideas-price',
    family: 'ideas',
    slot: 'cutout',
    category: 'promo',
    format: 'ig-portrait',
    name: bi('Ideas — el precio manda', 'Ideas — price as hero'),
    build: (b) => {
      b.bg('primary');
      tx(b, 'price', 0, 170, 1080, 640, {
        family: 'Anton',
        size: 520,
        color: 'onPrimary',
        align: 'center',
        valign: 'middle',
        leading: 1,
      });
      b.photo(330, 600, 720, 720, { fit: 'contain', zoom: 0.95, shadow: DEEP });
      b.logo(60, 1190, 130, 130);
    },
  },
  // 18. Two-for-one mirror: the same dish twice, mirrored, "2x1" between them.
  {
    id: 'ideas-mirror',
    family: 'ideas',
    slot: 'cutout',
    category: 'promo',
    format: 'ig-square',
    name: bi('Ideas — 2x1 en espejo', 'Ideas — two-for-one mirror'),
    ad: { headline: esOnly('2x1') },
    build: (b) => {
      b.bg('primary');
      b.photo(0, 200, 430, 740, { fit: 'contain', zoom: 0.95, shadow: DEEP });
      b.photo(650, 200, 430, 740, { fit: 'contain', zoom: 0.95, shadow: DEEP, flipX: true });
      tx(b, 'headline', 290, 380, 500, 300, {
        family: 'Anton',
        size: 250,
        color: 'onPrimary',
        align: 'center',
        valign: 'middle',
        leading: 1,
      });
      tx(b, 'price', 290, 950, 500, 90, {
        family: 'Oswald',
        weight: 700,
        size: 72,
        color: 'onPrimary',
        align: 'center',
      });
      b.logo(475, 40, 130, 130);
    },
  },
  // 19. Question and answer: the question on top, both dishes below, the answer.
  {
    id: 'ideas-qa',
    family: 'ideas',
    slot: 'cutout',
    category: 'story',
    format: 'story',
    name: bi('Ideas — pregunta y respuesta', 'Ideas — question and answer'),
    ad: { headline: esOnly('¿Pollo o carne?'), tagline: esOnly('¿Por qué no los dos?') },
    adAny: { headline: esOnly('¿Este o aquel? ¿Por qué no los dos?'), tagline: esOnly('Antojo resuelto.') },
    build: (b) => {
      b.bg('secondary');
      b.rect(0, 0, 1080, 940, { fill: 'primary' });
      tx(b, 'headline', 60, 300, 960, 320, {
        family: 'Anton',
        size: 150,
        color: 'onPrimary',
        align: 'center',
        maxLines: 2,
        leading: 1,
      });
      tx(b, 'tagline', 60, 660, 960, 110, {
        family: 'Oswald',
        weight: 600,
        size: 72,
        color: 'onPrimarySmall',
        align: 'center',
      });
      b.photo(0, 960, 540, 760, { fit: 'contain', zoom: 0.92, shadow: DEEP });
      b.photo(540, 960, 540, 760, { fit: 'contain', zoom: 0.92, shadow: DEEP, own: true });
      b.logo(475, 790, 130, 130);
    },
  },
  // 20. Speech bubble: the dish talks.
  {
    id: 'ideas-bubble',
    family: 'ideas',
    slot: 'cutout',
    category: 'post',
    format: 'ig-square',
    name: bi('Ideas — globo de diálogo', 'Ideas — speech bubble'),
    ad: { headline: esOnly('¿Qué espera? ¡Pídame!') },
    build: (b) => {
      b.bg('primary');
      b.photo(20, 360, 720, 700, { fit: 'contain', zoom: 0.95, shadow: DEEP });
      b.rotate(b.rect(515, 325, 90, 90, { fill: WHITE }), 45, 560, 370);
      b.rect(400, 70, 620, 300, { fill: WHITE, r: 70 });
      tx(b, 'headline', 440, 100, 540, 240, {
        family: 'Oswald',
        weight: 700,
        size: 64,
        color: INK,
        align: 'center',
        valign: 'middle',
        maxLines: 3,
        leading: 1.05,
      });
      tx(b, 'price', 720, 950, 320, 90, {
        family: 'Oswald',
        weight: 700,
        size: 72,
        color: 'onPrimary',
        align: 'right',
      });
      b.logo(40, 40, 120, 120);
    },
  },
  // 21. Receipt: the ad as a printed till ticket; the total is the price.
  {
    id: 'ideas-receipt',
    family: 'ideas',
    slot: 'cutout',
    category: 'post',
    format: 'ig-portrait',
    name: bi('Ideas — ticket de caja', 'Ideas — receipt'),
    build: (b) => {
      b.bg('secondary');
      b.rect(150, 30, 780, 1290, { fill: WHITE });
      // The torn bottom edge: notches in the background colour.
      for (let x = 150; x <= 930; x += 52)
        b.rotate(b.rect(x - 20, 1300, 40, 40, { fill: 'secondary' }), 45, x, 1320);
      b.photo(170, 60, 740, 700, { fit: 'contain', zoom: 0.92, shadow: SOFT });
      const mono = { family: 'Courier Prime', color: INK } as const;
      b.text(190, 780, 700, 50, {
        role: 'restaurant',
        ...mono,
        weight: 700,
        size: 40,
        align: 'center',
        caps: true,
        maxLines: 1,
      });
      b.line(190, 850, 890, 850, INK, 2, [10, 8]);
      tx(b, 'dishName', 190, 875, 440, 100, { ...mono, size: 36, maxLines: 2 });
      tx(b, 'price', 640, 875, 250, 50, { ...mono, size: 36, align: 'right' });
      b.line(190, 1000, 890, 1000, INK, 2, [10, 8]);
      b.text(190, 1025, 300, 60, {
        role: 'free',
        text: 'TOTAL',
        ...mono,
        weight: 700,
        size: 48,
        maxLines: 1,
      });
      tx(b, 'price', 500, 1025, 390, 60, { ...mono, weight: 700, size: 48, align: 'right' });
      b.text(190, 1105, 700, 46, {
        role: 'free',
        text: 'Gracias por su visita',
        ...mono,
        color: MUTED,
        size: 34,
        align: 'center',
        maxLines: 1,
      });
      b.logo(480, 1165, 120, 120, { onLight: true });
    },
  },
  // 22. Map pin: the dish on a big location pin; the location line from My brand.
  {
    id: 'ideas-pin',
    family: 'ideas',
    slot: 'cutout',
    category: 'post',
    format: 'ig-square',
    name: bi('Ideas — pin del mapa', 'Ideas — map pin'),
    ad: { headline: esOnly('Lo encuentra aquí.') },
    build: (b) => {
      b.bg('white');
      // A faint street map.
      for (const x of [170, 420, 760, 960]) b.line(x, 0, x, 1080, '#ececec', 6);
      for (const y of [250, 560, 830]) b.line(0, y, 1080, y, '#ececec', 6);
      b.rotate(b.rect(390, 420, 300, 300, { fill: 'primary' }), 45, 540, 570);
      b.rect(240, 80, 600, 600, { fill: 'primary', r: 300 });
      b.photo(190, 30, 700, 600, { fit: 'contain', zoom: 0.95, shadow: DEEP });
      b.text(60, 850, 960, 64, {
        role: 'location',
        lang: 'es',
        family: 'Oswald',
        weight: 600,
        size: 52,
        color: 'accentText',
        align: 'center',
        maxLines: 1,
      });
      tx(b, 'headline', 60, 930, 960, 100, {
        family: 'Oswald',
        weight: 700,
        size: 80,
        color: 'accentText',
        align: 'center',
      });
      b.logo(40, 40, 120, 120, { onLight: true });
    },
  },
  // 23. Stack of prints: three tilted photos of the dish, different crops; one handwritten word.
  {
    id: 'ideas-prints',
    family: 'ideas',
    slot: 'full',
    category: 'post',
    format: 'ig-portrait',
    name: bi('Ideas — fotos instantáneas', 'Ideas — stack of prints'),
    ad: { headline: esOnly('¡Delicioso!') },
    adAny: { headline: esOnly('Para chuparse los dedos.') },
    build: (b) => {
      b.bg('secondary');
      print(b, 330, 420, -8, { zoom: 1.2, x: 0, y: 0 });
      print(b, 750, 520, 7, { zoom: 1.9, x: 0.15, y: -0.1 });
      print(b, 520, 820, -3, { zoom: 2.6, x: -0.1, y: 0.15 });
      tx(b, 'headline', 60, 1190, 780, 130, {
        family: 'Caveat',
        weight: 700,
        size: 110,
        color: 'onSecondary',
      });
      b.logo(900, 1200, 130, 130);
    },
  },
  // 24. Clock: the hands point at the dish.
  {
    id: 'ideas-clock',
    family: 'ideas',
    slot: 'cutout',
    category: 'post',
    format: 'ig-square',
    name: bi('Ideas — reloj', 'Ideas — clock'),
    ad: { headline: esOnly('Hora de comer.') },
    build: (b) => {
      b.bg('white');
      const cx = 340,
        cy = 340;
      b.rect(60, 60, 560, 560, { fill: WHITE, stroke: 'secondary', strokeW: 12, r: 280 });
      for (let i = 0; i < 12; i++) {
        const a = (i * Math.PI) / 6;
        b.line(
          cx + Math.cos(a) * 225,
          cy + Math.sin(a) * 225,
          cx + Math.cos(a) * 255,
          cy + Math.sin(a) * 255,
          'secondary',
          8,
        );
      }
      const hand = (deg: number, len: number, w: number, c: 'primary' | 'secondary') => {
        const a = (deg * Math.PI) / 180;
        b.line(cx, cy, cx + Math.cos(a) * len, cy + Math.sin(a) * len, c, w);
      };
      hand(48, 150, 18, 'secondary');
      hand(36, 230, 12, 'primary');
      b.rect(cx - 20, cy - 20, 40, 40, { fill: 'secondary', r: 20 });
      b.photo(460, 400, 620, 660, { fit: 'contain', zoom: 0.95, shadow: DEEP });
      tx(b, 'headline', 60, 700, 420, 190, {
        family: 'Oswald',
        weight: 700,
        size: 76,
        color: 'accentText',
        maxLines: 2,
        leading: 1.05,
      });
      tx(b, 'price', 60, 920, 400, 100, { family: 'Oswald', weight: 700, size: 72, color: 'accentText' });
      b.logo(930, 40, 120, 120, { onLight: true });
    },
  },
  // 25. Tiny dish, huge space: a very small dish in a sea of flat colour, one line.
  {
    id: 'ideas-tiny',
    family: 'ideas',
    slot: 'cutout',
    category: 'post',
    format: 'ig-portrait',
    name: bi('Ideas — platillo diminuto', 'Ideas — tiny dish'),
    ad: { headline: esOnly('Pequeño pecado. Gran sabor.') },
    build: (b) => {
      b.bg('primary');
      // M21b review: about twice the first size, so the space reads as intended.
      b.photo(360, 430, 360, 360, { fit: 'contain', shadow: SOFT });
      tx(b, 'headline', 60, 1160, 960, 60, {
        family: 'Montserrat',
        weight: 700,
        size: 40,
        color: 'onPrimarySmall',
        align: 'center',
      });
      b.logo(60, 60, 110, 110);
    },
  },
  // 26. Day of the week: a calendar page with a big day name, the dish, the offer.
  {
    id: 'ideas-weekday',
    family: 'ideas',
    slot: 'cutout',
    category: 'promo',
    format: 'story',
    name: bi('Ideas — especial del día de la semana', 'Ideas — weekly special'),
    ad: { headline: esOnly('de 2x1.'), tagline: esOnly('MARTES') },
    build: (b) => {
      b.bg('primary');
      b.rect(110, 300, 860, 1060, { fill: WHITE, r: 24 });
      b.rect(110, 300, 860, 200, { fill: 'secondary', r: 24 });
      b.rect(110, 420, 860, 80, { fill: 'secondary' });
      b.rect(300, 270, 40, 80, { fill: WHITE, r: 20 });
      b.rect(740, 270, 40, 80, { fill: WHITE, r: 20 });
      tx(b, 'tagline', 110, 330, 860, 160, {
        family: 'Anton',
        size: 150,
        color: 'onSecondary',
        align: 'center',
        valign: 'middle',
        caps: true,
        leading: 1,
      });
      b.photo(110, 500, 860, 860, { fit: 'contain', zoom: 0.92, shadow: SOFT });
      tx(b, 'headline', 110, 1380, 760, 140, {
        family: 'Anton',
        size: 120,
        color: 'onPrimary',
        leading: 1,
      });
      b.logo(900, 1396, 130, 130);
    },
  },
];
